import LocationInput, {
  LocationInputHandle,
} from 'components/authorable/shared/site-structure/Header/LocationInput';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Location } from 'components/authorable/shared/site-structure/Header/LocationInput';
import { Field, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { SearchProvider } from 'lib/utils/coveo/SearchProvider';
import { SearchBox, SearchBoxHandle } from './SearchBox';
import { buildSearchBox, loadAdvancedSearchQueryActions } from '@coveo/headless';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { useCoveoEngineControls } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useRouter } from 'next/router';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';

type SearchConfig = {
  searchHub: string;
  pipeline: string;
  accessToken: string;
  organizationId: string;
};

type SearchHeaderWidgetProps = {
  locationSearchPlaceholderText?: Field<string>;
  searchButtonLabel?: string;
  searchTermPlaceholderText?: Field<string>;
  searchLink?: LinkField;
  searchConfig: SearchConfig;
  shouldRedirect?: boolean;
  searchFields?: string[];
  fieldsToInclude?: string[];
  onLocationCoordinatesChange?: (
    coords: {
      latitude: number | null;
      longitude: number | null;
    } | null
  ) => void;
  onSearchTermChange?: (term: string) => void;
  clearOnLoad?: boolean;
  hasCoveoWrapper?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Separate component for the search interface
const SearchInterface: React.FC<{
  searchBoxRef: React.RefObject<SearchBoxHandle>;
  locationRef: React.RefObject<LocationInputHandle>;
  isSearchFocused: boolean;
  setIsSearchFocused: (value: boolean) => void;
  isLocationFocused: boolean;
  setIsLocationFocused: (value: boolean) => void;
  searchBorderColor: string;
  locationBorderColor: string;
  location: Location | null;
  setLocation: (location: Location | null) => void;
  handleSearchClick: () => void;
  hasCoveoWrapper?: boolean;
  props: SearchHeaderWidgetProps;
}> = ({
  searchBoxRef,
  locationRef,
  isSearchFocused,
  setIsSearchFocused,
  isLocationFocused,
  setIsLocationFocused,
  searchBorderColor,
  locationBorderColor,
  location,
  setLocation,
  handleSearchClick,
  hasCoveoWrapper = false,
  props,
}) => {
  return (
    <div className="lg:flex lg:mt-[40px] mt-[32px] gap-5">
      <div className="lg:flex w-full gap rounded-sm ">
        <div
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          className={`w-full lg:w-1/2  lg:border-b-2 border-b-0 lg:border-r-0 border-r-2 border-2 ${searchBorderColor} rounded-tl-sm rounded-bl-sm lg:rounded-tr-none rounded-tr-sm lg:rounded-br-none transition-colors duration-200`}
        >
          <SearchBox
            ref={searchBoxRef}
            placeholder={props?.searchTermPlaceholderText}
            onSubmitRedirect={false}
            onClear={() => setIsSearchFocused(false)}
            onSubmit={handleSearchClick}
            hasCoveoWrapper={hasCoveoWrapper}
          />
        </div>
        <div
          className={`h-[2px] lg:h-auto lg:w-[2px] w-full transition-colors duration-200 ${isSearchFocused || isLocationFocused ? 'bg-blue-base' : 'bg-gray-100'}`}
        ></div>
        <div
          onFocus={() => setIsLocationFocused(true)}
          onBlur={() => setIsLocationFocused(false)}
          className={`w-full lg:w-1/2 border-t-0 lg:border-t-2 lg:border-l-0 border-2 ${locationBorderColor} rounded-tr-sm rounded-br-sm lg:rounded-tl-none rounded-bl-sm lg:rounded-bl-none transition-colors duration-200`}
        >
          <LocationInput
            ref={locationRef}
            value={location ?? undefined}
            onLocationSelect={(loc: Location) => {
              setLocation(loc);
              // Push coordinates up so ProviderSearch updates finalCoordinates
              if (props.onLocationCoordinatesChange) {
                if (loc.lat != null && loc.lng != null) {
                  props.onLocationCoordinatesChange({ latitude: loc.lat, longitude: loc.lng });
                } else {
                  props.onLocationCoordinatesChange(null);
                }
              }
              // New coordinates selected: clear any manual sort override so distance can re-apply
              try {
                if (typeof window !== 'undefined') {
                  window.sessionStorage.removeItem('coveoUserSortOverride');
                }
              } catch {}
            }}
            placeholder={props?.locationSearchPlaceholderText}
            onClear={() => {
              setIsLocationFocused(false);
              setLocation(null);
              // Propagate cleared coordinates upwards
              if (props.onLocationCoordinatesChange) {
                props.onLocationCoordinatesChange(null);
              }
              // Disable distance mode and reset manual override
              try {
                if (typeof window !== 'undefined') {
                  window.sessionStorage.removeItem('coveoUserSortOverride');
                }
              } catch {}
            }}
            onEnter={handleSearchClick}
          />
        </div>
      </div>

      {props.shouldRedirect ? (
        <LinkWrapper
          ctaVariant="primary"
          className="px-12 py-4 font-semibold text-white bg-blue-600 hover:bg-blue-700 mt-5 lg:mt-0 w-full lg:w-fit"
          ctaIcon="search"
          ctaIconAlignment="left"
          field={props?.searchLink}
          onClick={handleSearchClick}
        >
          {props?.searchButtonLabel}
        </LinkWrapper>
      ) : (
        <ButtonWrapper
          ctaVariant="primary"
          className="px-12 py-4 font-semibold text-white bg-blue-600 hover:bg-blue-700 mt-5 lg:mt-0 w-full lg:w-fit"
          ctaIcon="search"
          ctaIconAlignment="left"
          onClick={handleSearchClick}
        >
          {props?.searchButtonLabel}
        </ButtonWrapper>
      )}
    </div>
  );
};

const SearchHeaderWidget = (props: SearchHeaderWidgetProps) => {
  const router = useRouter();
  const targetHref = props.searchLink?.value?.href || '/';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const searchBoxRef = useRef<SearchBoxHandle>(null);
  const locationRef = useRef<LocationInputHandle>(null);
  const { setContextResults } = useSearch();
  const engine = useCoveoEngine();
  const engineControls = useCoveoEngineControls();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  //const [hasAutoSearched, _setHasAutoSearched] = useState(false);
  //const { sitecoreContext } = useSitecoreContext();
  const searchBorderColor = isSearchFocused ? 'border-blue-base' : 'border-gray-100';
  const locationBorderColor = isLocationFocused ? 'border-blue-base' : 'border-gray-100';
  //const { searchHeaderData, setSearchHeaderData } = useSearch();
  const escapeForCoveo = (str: string = ''): string => {
    return str.replace(/([+\-&|!(){}[\]^"~?:\\/])/g, '\\$1');
  };
  //const currentPage = (sitecoreContext?.itemPath as string).toLowerCase();
  const handleSearchClick = useCallback(() => {
    const searchTerm = searchBoxRef.current?.getCurrentValue()?.trim();
    const location = locationRef.current?.getLocationValue();
    const locationTerm = location?.address?.trim();
    const locationLatitude = location?.lat?.toString();
    const locationLongitude = location?.lng?.toString();

    if (searchTerm && props.onSearchTermChange) {
      props.onSearchTermChange(searchTerm);
    }
    if (props.onLocationCoordinatesChange) {
      if (locationLatitude && locationLongitude) {
        props.onLocationCoordinatesChange({
          latitude: parseFloat(locationLatitude),
          longitude: parseFloat(locationLongitude),
        });
      } else {
        props.onLocationCoordinatesChange(null);
      }
    }

    // Update URL if needed (as in your code)
    const currentQuery = router.query;
    const query: Record<string, string> = {};
    if (searchTerm) query.q = searchTerm;
    if (locationTerm) query.loc = locationTerm;
    if (locationLatitude) query.lat = locationLatitude;
    if (locationLongitude) query.lng = locationLongitude;
    const currentLoc = (currentQuery.loc as string) || '';
    const currentLat = (currentQuery.lat as string) || '';
    const currentLng = (currentQuery.lng as string) || '';
    const currentQ = (currentQuery.q as string) || '';
    const hasUrlChanged =
      currentLoc !== locationTerm ||
      currentLat !== locationLatitude ||
      currentLng !== locationLongitude ||
      currentQ !== searchTerm;
    if (props.shouldRedirect && targetHref && currentPath !== targetHref) {
      router.push({
        pathname: targetHref,
        query,
      });
      return;
    }
    if (!props.shouldRedirect && hasUrlChanged) {
      router.push(
        {
          pathname: window.location.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    }

    // if engine config not found
    if (!engine) return;

    const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

    const aqParts: string[] = [];

    // Dynamically build searchTerm part of the AQ
    if (searchTerm && props.searchFields?.length) {
      const escapedSearchTerm = escapeForCoveo(searchTerm);
      const searchConditions = props.searchFields.map(
        (field) => `@${field}==*${escapedSearchTerm}*`
      );
      aqParts.push(`(${searchConditions.join(' OR ')})`);
    }

    const finalAQ = aqParts.join(' AND '); // Combine with AND when both exist
    engine.dispatch(updateAdvancedSearchQueries({ cq: aqParts.length ? finalAQ : '' }));

    // Apply sort rules based on presence of coordinates
    if (locationLatitude && locationLongitude) {
      // New coordinates: enable distance mode and clear manual override
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem('coveoUserSortOverride');
        }
      } catch {}
      engineControls?.setDistanceOverride('on');
      engine.dispatch({
        type: 'sort/updateSortCriterion',
        payload: { by: 'field', field: 'distanceinmiles', direction: 'ascending' },
      });
    } else {
      // No coordinates: disable distance mode, reset to default (relevancy)
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem('coveoUserSortOverride');
        }
      } catch {}
      engineControls?.setDistanceOverride('off');
      engine.dispatch({
        type: 'sort/updateSortCriterion',
        payload: { by: 'relevance' },
      });
    }

    const controller = buildSearchBox(engine, { options: { numberOfSuggestions: 5 } });
    controller.updateText(searchTerm || '');
    controller.submit();

    engine.subscribe(() => {
      const fullSearchState = engine.state.search;
      setContextResults(fullSearchState);
    });
  }, [currentPath, engine, engineControls, props, router, setContextResults, targetHref]);

  // clear on load on searchForDoctorNearYou
  useEffect(() => {
    if (props.clearOnLoad) {
      searchBoxRef.current?.setCurrentValue('');
      locationRef.current?.setLocationValue({
        address: '',
        lat: null,
        lng: null,
      });
    }
  }, [props.clearOnLoad]);

  const [location, setLocation] = useState<Location | null>(null);
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearchClick();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSearchClick, props.shouldRedirect]);

  //this is the actual defect: if this enables distance sorting not working.
  const hasHandledInitialSearch = useRef(false);

  useEffect(() => {
    if (hasHandledInitialSearch.current) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    const loc = params.get('loc');
    const lat = params.get('lat');
    const lng = params.get('lng');

    const hasLatLng = lat && lng;

    // Pre-fill search term and location if present
    if (q) searchBoxRef.current?.setCurrentValue(q);
    if (loc && hasLatLng) {
      locationRef.current?.setLocationValue({
        address: loc,
        lat: lat ? parseFloat(lat) || 0 : 0,
        lng: lng ? parseFloat(lng) || 0 : 0,
      });
    }
    // else if (!loc && !hasLatLng) {
    //   //debugger;
    //   // Clear location, and optionally run a fallback search
    //   locationRef.current?.setLocationValue({
    //     address: '',
    //     lat: null,
    //     lng: null,
    //   });

    const shouldRunSearch = !props.shouldRedirect && (q || loc || lat || lng);
    if (shouldRunSearch) {
      hasHandledInitialSearch.current = true;
      // Optional: run a fallback search without distance sort
      setTimeout(() => {
        handleSearchClick();
      }, 150);
    }
    // }

    // const shouldRunSearch = !props.shouldRedirect && (q || loc);
    // //const cleanUrl = `${window.location.origin}${currentPage}`;

    // if (shouldRunSearch) {
    //   setTimeout(() => {
    //     handleSearchClick();
    //   }, 150);
    // }
  }, [handleSearchClick, props.shouldRedirect]);
  const searchInterfaceProps = {
    searchBoxRef,
    locationRef,
    isSearchFocused,
    setIsSearchFocused,
    isLocationFocused,
    setIsLocationFocused,
    searchBorderColor,
    locationBorderColor,
    location,
    setLocation,
    handleSearchClick,
    props,
  };

  return (
    <>
      {props.hasCoveoWrapper ? (
        <SearchInterface {...searchInterfaceProps} hasCoveoWrapper={props.hasCoveoWrapper} />
      ) : (
        <SearchProvider
          searchHub={props?.searchConfig?.searchHub}
          pipeline={props?.searchConfig?.pipeline}
          accessToken={props?.searchConfig?.accessToken}
          organizationId={props?.searchConfig?.organizationId}
          analyticsEnabled={false}
        >
          <SearchInterface {...searchInterfaceProps} hasCoveoWrapper={props.hasCoveoWrapper} />
        </SearchProvider>
      )}
    </>
  );
};

export default SearchHeaderWidget;
